import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, Tag, Brain, MessageSquare, FileText, Sparkles } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';

interface AIPrompt {
  id: string;
  name: string;
  description?: string;
  prompt_type: string;
  template_category?: string;
  content: string;
  version: string;
  is_active: boolean;
  is_default: boolean;
  tags: string[];
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  description: string;
  prompt_type: string;
  template_category: string;
  content: string;
  version: string;
  is_active: boolean;
  is_default: boolean;
  tags: string[];
  metadata: any;
}

const AIPromptEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prompt, setPrompt] = useState<AIPrompt | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    prompt_type: 'analysis',
    template_category: '',
    content: '',
    version: '1.0',
    is_active: true,
    is_default: false,
    tags: [],
    metadata: {}
  });

  const promptTypes = [
    { value: 'analysis', label: '分析类', icon: Brain, color: 'blue' },
    { value: 'chat', label: '聊天类', icon: MessageSquare, color: 'green' },
    { value: 'summary', label: '摘要类', icon: FileText, color: 'purple' },
    { value: 'evaluation', label: '评估类', icon: Sparkles, color: 'orange' }
  ];

  const templateCategories = [
    'deep_insight',
    'news_summary', 
    'tutor',
    'review',
    'recommendation',
    'custom'
  ];

  useEffect(() => {
    if (id && id !== 'new') {
      fetchPrompt();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchPrompt = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=prompts&action=detail&id=${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        const promptData = result.data;
        setPrompt(promptData);
        setFormData({
          name: promptData.name,
          description: promptData.description || '',
          prompt_type: promptData.prompt_type,
          template_category: promptData.template_category || '',
          content: promptData.content,
          version: promptData.version,
          is_active: promptData.is_active,
          is_default: promptData.is_default,
          tags: promptData.tags || [],
          metadata: promptData.metadata || {}
        });
      } else {
        console.error('获取提示词失败:', result.error);
        toast.error('获取提示词失败');
        navigate('/admin/prompts');
      }
    } catch (error) {
      console.error('获取提示词失败:', error);
      toast.error('获取提示词失败');
      navigate('/admin/prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      const isNew = !id || id === 'new';
      const url = isNew 
        ? `${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=prompts&action=create`
        : `${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=prompts&action=update`;
      
      const method = isNew ? 'POST' : 'PUT';
      const body = isNew 
        ? formData
        : { id, ...formData };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(isNew ? '提示词创建成功' : '提示词更新成功');
      navigate('/admin/prompts');
    } catch (error) {
      console.error('保存提示词失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTagsChange = (tagsString: string) => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData({ ...formData, tags });
  };

  const getTypeInfo = (type: string) => {
    return promptTypes.find(t => t.value === type) || promptTypes[0];
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="ml-4 text-gray-600">正在加载...</p>
        </div>
      </AdminLayout>
    );
  }

  const typeInfo = getTypeInfo(formData.prompt_type);
  const TypeIcon = typeInfo.icon;
  const isNew = !id || id === 'new';

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/admin/prompts')}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  返回列表
                </button>
                <div className="h-6 border-l border-gray-300"></div>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-${typeInfo.color}-100`}>
                    <TypeIcon className={`w-6 h-6 text-${typeInfo.color}-600`} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {isNew ? '创建提示词' : '编辑提示词'}
                    </h1>
                    {!isNew && (
                      <p className="text-sm text-gray-500">
                        {prompt?.name} - {typeInfo.label}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitting ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* 基本信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">基本信息</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    名称 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入提示词名称"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    类型 *
                  </label>
                  <select
                    required
                    value={formData.prompt_type}
                    onChange={(e) => setFormData({ ...formData, prompt_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {promptTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模板分类
                  </label>
                  <select
                    value={formData.template_category}
                    onChange={(e) => setFormData({ ...formData, template_category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">请选择分类</option>
                    {templateCategories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    版本
                  </label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1.0"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入提示词描述"
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  标签 (用逗号分隔)
                </label>
                <input
                  type="text"
                  value={formData.tags.join(', ')}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="例如: 分析, 商业, 专业"
                />
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 flex items-center text-sm text-gray-900">
                    {formData.is_active ? (
                      <><Eye className="w-4 h-4 mr-1" />启用此提示词</>
                    ) : (
                      <><EyeOff className="w-4 h-4 mr-1" />禁用此提示词</>
                    )}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                    设为默认提示词
                  </label>
                </div>
              </div>
            </div>

            {/* 提示词内容 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">提示词内容</h2>
              <div className="space-y-4">
                <div data-color-mode="light">
                  <MDEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value || '' })}
                    preview="edit"
                    hideToolbar={false}
                    visibleDragBar={false}
                    height={500}
                    data-testid="md-editor"
                  />
                </div>
                <p className="text-sm text-gray-500">
                  支持 Markdown 语法，可以使用 **粗体**、*斜体*、`代码`、列表等格式。
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AIPromptEdit;